import { useMemo } from 'react';
import { useNodes } from '@xyflow/react';
import { Select } from 'antd';
import { CustomSelectIcon } from '@/assets/icons/icons';
import { DelayEventWhere } from '@/proto/logiblocks/logiblocks';
import type { ReactFlowNode } from '@/pages/Logiblocks/types';
import {
  runAlertOf,
  WHERE_RUN_ALERT_PLAIN_LABEL,
  WHERE_SHIPMENT_LABEL,
} from './delayUntilEventModel';
import styles from './DelayUntilEvent.module.css';

interface DelayWhereSelectProps {
  value: DelayEventWhere;
  onChange: (where: DelayEventWhere) => void;
  disabled?: boolean;
}

/**
 * "Where" picker of Document added and Note added: on the alert that started the run, or anywhere
 * on the shipment's chain of custody. The first names the alert type when the trigger is for one
 * ("Appended to **Customs hold** alert"), and is offered only in a workflow that starts from an
 * alert (a saved choice of it stays visible, so the publish error has something to point at).
 * Reads the graph through React Flow, so it only works under a React Flow provider.
 */
export const DelayWhereSelect = ({
  value,
  onChange,
  disabled = false,
}: DelayWhereSelectProps) => {
  const nodes = useNodes<ReactFlowNode>();
  const { startsFromAlert, alertName } = useMemo(
    () => runAlertOf(nodes),
    [nodes]
  );

  const options = useMemo(() => {
    const runAlert = {
      value: DelayEventWhere.DELAY_EVENT_WHERE_RUN_ALERT,
      label: alertName ? (
        <>
          Appended to <strong>{alertName}</strong> alert
        </>
      ) : (
        WHERE_RUN_ALERT_PLAIN_LABEL
      ),
    };
    const shipment = {
      value: DelayEventWhere.DELAY_EVENT_WHERE_SHIPMENT,
      label: WHERE_SHIPMENT_LABEL,
    };
    const showRunAlert =
      startsFromAlert || value === DelayEventWhere.DELAY_EVENT_WHERE_RUN_ALERT;
    return showRunAlert ? [runAlert, shipment] : [shipment];
  }, [startsFromAlert, alertName, value]);

  const chosen =
    value === DelayEventWhere.DELAY_EVENT_WHERE_RUN_ALERT ||
    value === DelayEventWhere.DELAY_EVENT_WHERE_SHIPMENT
      ? value
      : undefined;

  return (
    <div>
      <label className={styles.fieldLabel}>Where</label>
      <Select
        aria-label='Where'
        value={chosen}
        onChange={onChange}
        options={options}
        disabled={disabled}
        suffixIcon={<CustomSelectIcon />}
        placeholder='Select'
        className='agentSelect agentSelect--darkText'
        style={{ width: '100%' }}
      />
    </div>
  );
};
